import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@patient-watch/ui/tooltip";
import dayjs from "dayjs";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";

export type MessageTimelineStatus = "sent" | "scheduled" | "cancelled" | "skipped" | "event";

export type MessageWorkflowRunStatus = "running" | "completed" | "cancelled" | "skipped";

export interface MessageTimelineEvent {
    id: string;
    at: string;
    workflowKey: string;
    /** Separate swimlane when multiple runs share a workflowKey (e.g. two new-diary runs). */
    laneKey?: string;
    workflowName?: string;
    label?: string;
    channel?: "email" | "sms" | "push" | "in-app" | "unknown";
    /** Short subject / body for tooltip hover. */
    preview?: string;
    status?: MessageTimelineStatus;
    /** Parent workflow run status - used for open-ended “expecting more” tails. */
    workflowStatus?: MessageWorkflowRunStatus;
}

export interface MessageTimelineChartProps {
    events: MessageTimelineEvent[];
    variant?: "spark" | "detail";
    className?: string;
    /** Optional shared domain so table sparks align across rows. */
    domainStartMs?: number;
    domainEndMs?: number;
    showLines?: boolean;
    /** Detail variant: show the subject/date panel under the swimlanes. Default true. */
    showSelectionPanel?: boolean;
}

export const ACCOUNT_EVENTS_KEY = "account-events";

/**
 * Patient Watch chart palette (blue / cyan / indigo / sky),
 * aligned with packages/charts trackerColors.
 */
export function laneKeyForEvent(event: MessageTimelineEvent): string {
    return event.laneKey ?? event.workflowKey;
}

export const WORKFLOW_TIMELINE_COLORS: Record<string, string> = {
    "auth-workflow": "#8b5cf6", // violet-500
    "invited-user-workflow": "#3b82f6", // blue-500
    "invited-secretary-workflow": "#6366f1", // indigo-500
    "new-diary-workflow": "#06b6d4", // cyan-500
    "clinician-roundup-workflow": "#0ea5e9", // sky-500
    "cs-retention-roundup": "#0ea5e9",
    "cs-account-invited": "#3b82f6",
    "cs-account-self-signup": "#6366f1",
    "cs-account-invited-secretary": "#818cf8",
    "cs-activation-templates": "#38bdf8",
    "cs-activation-first-diary": "#22d3ee",
    "cs-retention-winback": "#818cf8",
    "email-bounced-alert": "#f59e0b",
    "clinician-activation / no_templates": "#38bdf8", // sky-400
    "clinician-activation / no_diary": "#22d3ee", // cyan-400
    "clinician-winback (planned)": "#818cf8", // indigo-400
    "clinician-winback": "#818cf8",
    "clinician-activation (planned)": "#60a5fa", // blue-400
    "entry-reminders-workflow": "#0d9488", // teal-600
    "entry-reminder-workflow": "#0d9488",
    // Channel swimlanes (messaging schedule / multi-channel runs)
    email: "#3b82f6", // blue-500 - matches patient message history
    sms: "#22c55e", // green-500
    "in-app": "#64748b", // slate-500
    push: "#a855f7", // purple-500
    [ACCOUNT_EVENTS_KEY]: "#4f46e5", // indigo-600
};

const CHANNEL_LANE_ORDER = ["email", "sms", "in-app", "push", "unknown"] as const;

export function colorForChannel(channel: MessageTimelineEvent["channel"] | undefined): string | null {
    if (!channel || channel === "unknown") return null;
    return WORKFLOW_TIMELINE_COLORS[channel] ?? null;
}

const FALLBACK_PALETTE = ["#3b82f6", "#06b6d4", "#6366f1", "#0ea5e9", "#60a5fa", "#22d3ee", "#818cf8", "#38bdf8"];
const ACCOUNT_EVENT_STROKE = "#4f46e5";

export function colorForWorkflow(workflowKey: string): string {
    const known = WORKFLOW_TIMELINE_COLORS[workflowKey];
    if (known) return known;
    let hash = 0;
    for (let i = 0; i < workflowKey.length; i++) {
        hash = (hash * 31 + workflowKey.charCodeAt(i)) >>> 0;
    }
    return FALLBACK_PALETTE[hash % FALLBACK_PALETTE.length] ?? "#64748b";
}

export function isAccountTimelineEvent(event: MessageTimelineEvent): boolean {
    return event.workflowKey === ACCOUNT_EVENTS_KEY || event.status === "event";
}

interface PlotPoint {
    id: string;
    x: number;
    y: number;
    at: string;
    workflowKey: string;
    workflowName: string;
    label: string;
    preview?: string;
    channel?: MessageTimelineEvent["channel"];
    fill: string;
}

function channelTooltipLabel(channel: MessageTimelineEvent["channel"] | undefined): string | null {
    switch (channel) {
        case "email":
            return "Email";
        case "sms":
            return "SMS";
        case "in-app":
            return "In-app";
        case "push":
            return "Push";
        default:
            return null;
    }
}

function MessageEventTooltipBody({
    subject,
    at,
    channel,
    laneLabel,
}: {
    subject: string;
    at: string;
    channel?: MessageTimelineEvent["channel"];
    laneLabel?: string;
}) {
    const channelLabel = channelTooltipLabel(channel) ?? laneLabel;
    return (
        <div className="space-y-1 max-w-[16rem]">
            <p className="font-semibold text-slate-900 text-xs leading-snug">{subject}</p>
            <p className="tabular-nums text-slate-500 text-xs">{dayjs(at).format("ddd D MMM, HH:mm")}</p>
            {channelLabel && <p className="text-slate-500 text-xs">{channelLabel}</p>}
        </div>
    );
}

function MessageTimelineTooltip({
    active,
    payload,
}: {
    active?: boolean;
    payload?: Array<{ payload: PlotPoint | (MessageTimelineEvent & { x?: number }) }>;
}) {
    if (!active || !payload?.length) return null;
    const point = payload[0]?.payload as PlotPoint | undefined;
    if (!point) return null;

    return (
        <div className="bg-white/95 shadow-md px-2.5 py-1.5 border border-slate-200 rounded-md">
            <MessageEventTooltipBody
                subject={point.preview || point.label || "Message"}
                at={point.at || new Date(point.x).toISOString()}
                channel={point.channel}
                laneLabel={point.workflowName}
            />
        </div>
    );
}

function statusStyles(
    status: MessageTimelineStatus | undefined,
    workflowKey: string
): { dotClass: string; dotStyle?: CSSProperties } {
    switch (status) {
        case "cancelled":
            return { dotClass: "bg-slate-300 ring-[3px] ring-white opacity-60" };
        case "skipped":
            return { dotClass: "bg-slate-300 ring-[3px] ring-white opacity-50" };
        case "scheduled":
        case "sent":
        default:
            return {
                dotClass: "ring-[3px] ring-white shadow-sm",
                dotStyle: { backgroundColor: colorForWorkflow(workflowKey) },
            };
    }
}

interface LaneGroup {
    laneKey: string;
    workflowKey: string;
    workflowName: string;
    color: string;
    events: MessageTimelineEvent[];
}

function isVisibleMessage(status: MessageTimelineStatus | undefined): boolean {
    return status !== "cancelled" && status !== "skipped";
}

function splitTimelineEvents(events: MessageTimelineEvent[]): {
    messages: MessageTimelineEvent[];
    accountEvents: MessageTimelineEvent[];
} {
    const messages: MessageTimelineEvent[] = [];
    const accountEvents: MessageTimelineEvent[] = [];
    for (const event of events) {
        if (!event.at) continue;
        if (isAccountTimelineEvent(event)) {
            accountEvents.push(event);
        } else if (isVisibleMessage(event.status)) {
            messages.push(event);
        }
    }
    return { messages, accountEvents };
}

function laneExpectsMore(events: MessageTimelineEvent[]): boolean {
    if (events.some((event) => event.workflowStatus === "running")) return true;
    if (events.some((event) => event.status === "scheduled")) return true;
    return false;
}

/** Thick connector segments between consecutive visible messages on a lane. */
function activeSegments(events: MessageTimelineEvent[]): Array<{ start: number; end: number }> {
    const active = [...events].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

    if (active.length < 2) return [];

    const segments: Array<{ start: number; end: number }> = [];
    for (let i = 0; i < active.length - 1; i++) {
        const start = new Date(active[i]!.at).getTime();
        const end = new Date(active[i + 1]!.at).getTime();
        if (end > start) segments.push({ start, end });
    }
    return segments;
}

/** Short tail past the last message when the workflow is still expecting sends. */
function openEndedTail(
    events: MessageTimelineEvent[],
    domain: { start: number; end: number; span: number }
): { start: number; end: number } | null {
    if (!laneExpectsMore(events)) return null;
    const active = [...events].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    const last = active[active.length - 1];
    if (!last) return null;
    const start = new Date(last.at).getTime();
    const end = Math.min(domain.end, start + domain.span * 0.14);
    if (end <= start) return null;
    return { start, end };
}

function DetailSwimlanes({
    events,
    domainStartMs,
    domainEndMs,
    className,
    showSelectionPanel = true,
}: {
    events: MessageTimelineEvent[];
    domainStartMs?: number;
    domainEndMs?: number;
    className?: string;
    showSelectionPanel?: boolean;
}) {
    const { lanes, domain, ticks, defaultEvent, accountEvents } = useMemo(() => {
        const { messages, accountEvents: markers } = splitTimelineEvents(events);
        const sortedMessages = [...messages].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
        const sortedMarkers = [...markers].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

        const laneOrder: string[] = [];
        for (const event of sortedMessages) {
            const laneKey = laneKeyForEvent(event);
            if (!laneOrder.includes(laneKey)) laneOrder.push(laneKey);
        }

        const laneGroups: LaneGroup[] = laneOrder
            .map((laneKey) => {
                const laneEvents = sortedMessages.filter((event) => laneKeyForEvent(event) === laneKey);
                const workflowKey = laneEvents[0]?.workflowKey ?? laneKey;
                const channel = laneEvents[0]?.channel;
                const channelColor = colorForChannel(channel);
                // Prefer channel colour when the lane is a channel swimlane (laneKey === channel).
                const color =
                    channelColor && (laneKey === channel || laneKey.endsWith(`:${channel}`))
                        ? channelColor
                        : colorForWorkflow(workflowKey);
                return {
                    laneKey,
                    workflowKey,
                    workflowName: laneEvents[0]?.workflowName ?? workflowKey,
                    color,
                    events: laneEvents,
                };
            })
            .sort((a, b) => {
                const channelA = a.events[0]?.channel;
                const channelB = b.events[0]?.channel;
                const channelLaneA = channelA && a.laneKey === channelA;
                const channelLaneB = channelB && b.laneKey === channelB;
                if (channelLaneA && channelLaneB) {
                    return (
                        CHANNEL_LANE_ORDER.indexOf(channelA as (typeof CHANNEL_LANE_ORDER)[number]) -
                        CHANNEL_LANE_ORDER.indexOf(channelB as (typeof CHANNEL_LANE_ORDER)[number])
                    );
                }
                if (channelLaneA) return -1;
                if (channelLaneB) return 1;
                const timeA = new Date(a.events[0]?.at ?? 0).getTime();
                const timeB = new Date(b.events[0]?.at ?? 0).getTime();
                if (timeA !== timeB) return timeA - timeB;
                if (a.workflowKey === "auth-workflow") return -1;
                if (b.workflowKey === "auth-workflow") return 1;
                return 0;
            });

        const allTimed = [...sortedMessages, ...sortedMarkers];
        const xs = allTimed.map((event) => new Date(event.at).getTime());
        const minX = domainStartMs ?? (xs.length ? Math.min(...xs) : Date.now());
        const maxX = domainEndMs ?? (xs.length ? Math.max(...xs) : Date.now());
        const pad = Math.max((maxX - minX) * 0.08, 3_600_000);
        const start = minX - pad;
        const end = maxX + pad;
        const span = Math.max(end - start, 1);

        // Prefer ticks on real message stops when there are a manageable number of times.
        const stopTimes = [...new Set(sortedMessages.map((event) => new Date(event.at).getTime()))].sort(
            (a, b) => a - b
        );
        const axisTicks =
            stopTimes.length >= 2 && stopTimes.length <= 12
                ? stopTimes.map((t) => ({
                      t,
                      label: dayjs(t).format(stopTimes.length > 6 ? "D MMM" : "ddd D"),
                  }))
                : Array.from({ length: 5 }, (_, index) => {
                      const t = start + (span * index) / 4;
                      return { t, label: dayjs(t).format("D MMM") };
                  });

        const preferred = [...sortedMessages].reverse()[0] ?? sortedMarkers[sortedMarkers.length - 1] ?? null;

        return {
            lanes: laneGroups,
            domain: { start, end, span },
            ticks: axisTicks,
            defaultEvent: preferred,
            accountEvents: sortedMarkers,
        };
    }, [domainEndMs, domainStartMs, events]);

    const [activeEvent, setActiveEvent] = useState<MessageTimelineEvent | null>(defaultEvent);

    useEffect(() => {
        setActiveEvent(defaultEvent);
    }, [defaultEvent?.id]);

    if (!lanes.length && !accountEvents.length) {
        return (
            <div className={className ?? "flex justify-center items-center w-full h-48 text-muted-foreground text-sm"}>
                No messages
            </div>
        );
    }

    const leftPct = (iso: string) => {
        const x = new Date(iso).getTime();
        return `${((x - domain.start) / domain.span) * 100}%`;
    };

    return (
        <TooltipProvider delayDuration={120}>
            <div className={`flex flex-col ${className ?? "w-full"}`}>
                <div className="flex gap-4 mb-2 pl-44">
                    <div className="relative flex-1 h-6">
                        {ticks.map((tick) => (
                            <span
                                key={tick.t}
                                className="top-0 absolute font-semibold tabular-nums text-slate-600 text-xs -translate-x-1/2"
                                style={{ left: `${((tick.t - domain.start) / domain.span) * 100}%` }}
                            >
                                {tick.label}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="relative bg-white/80 shadow-sm border-2 border-slate-200 rounded-2xl overflow-visible">
                    {/* Account events: full-height vertical markers across all lanes */}
                    <div className="top-0 right-4 bottom-0 left-44 z-[5] absolute pointer-events-none">
                        {accountEvents.map((event) => {
                            const isSelected = activeEvent?.id === event.id;
                            return (
                                <Tooltip key={event.id}>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            className="group/event top-0 bottom-0 absolute flex justify-center w-4 -translate-x-1/2 pointer-events-auto"
                                            style={{ left: leftPct(event.at) }}
                                            aria-label={event.label ?? "Account event"}
                                            aria-pressed={isSelected}
                                            onMouseEnter={() => setActiveEvent(event)}
                                            onFocus={() => setActiveEvent(event)}
                                            onClick={() => setActiveEvent(event)}
                                        >
                                            <span
                                                className={`block h-full border-l-[3px] transition-[border-style,border-color] ${
                                                    isSelected
                                                        ? "border-solid border-indigo-700"
                                                        : "border-dashed border-indigo-500 group-hover/event:border-solid group-hover/event:border-indigo-700"
                                                }`}
                                            />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="z-[60]">
                                        <MessageEventTooltipBody
                                            subject={event.preview ?? event.label ?? "Journey start"}
                                            at={event.at}
                                            laneLabel="Start"
                                        />
                                    </TooltipContent>
                                </Tooltip>
                            );
                        })}
                    </div>

                    {lanes.length === 0 ? (
                        <div className="flex items-center px-4 py-10 min-h-[6.5rem] text-slate-500 text-sm">
                            No workflow messages yet
                        </div>
                    ) : (
                        lanes.map((lane, laneIndex) => (
                            <div
                                key={lane.laneKey}
                                className={laneIndex === 0 ? "flex gap-4" : "flex gap-4 border-t-2 border-slate-100"}
                            >
                                <div className="flex items-center gap-2.5 bg-slate-50/80 px-4 py-7 border-slate-200 border-r-2 w-44 shrink-0">
                                    <span
                                        className="shadow-sm rounded-full w-3 h-3 shrink-0"
                                        style={{ backgroundColor: lane.color }}
                                    />
                                    <span className="font-semibold text-slate-800 text-sm leading-snug">
                                        {lane.workflowName}
                                    </span>
                                </div>

                                <div className="relative flex-1 pr-4 min-h-[6.5rem]">
                                    <div
                                        className="top-1/2 right-4 left-0 absolute border-slate-300 border-t-2 border-dashed"
                                        aria-hidden
                                    />
                                    {ticks.map((tick) => (
                                        <div
                                            key={`${lane.laneKey}-${tick.t}`}
                                            className="top-3 bottom-3 absolute border-slate-200 border-l-2 border-dashed"
                                            style={{
                                                left: `${((tick.t - domain.start) / domain.span) * 100}%`,
                                            }}
                                            aria-hidden
                                        />
                                    ))}

                                    {activeSegments(lane.events).map((segment) => {
                                        const left = ((segment.start - domain.start) / domain.span) * 100;
                                        const width = ((segment.end - segment.start) / domain.span) * 100;
                                        return (
                                            <div
                                                key={`${lane.laneKey}-${segment.start}-${segment.end}`}
                                                className="top-1/2 z-[1] absolute rounded-full h-2.5 -translate-y-1/2"
                                                style={{
                                                    left: `${left}%`,
                                                    width: `${Math.max(width, 0.5)}%`,
                                                    backgroundColor: lane.color,
                                                }}
                                                aria-hidden
                                            />
                                        );
                                    })}

                                    {(() => {
                                        const tail = openEndedTail(lane.events, domain);
                                        if (!tail) return null;
                                        const left = ((tail.start - domain.start) / domain.span) * 100;
                                        const width = ((tail.end - tail.start) / domain.span) * 100;
                                        return (
                                            <div
                                                key={`${lane.laneKey}-open-tail`}
                                                className="top-1/2 z-[1] absolute opacity-55 rounded-r-full h-2.5 -translate-y-1/2"
                                                style={{
                                                    left: `${left}%`,
                                                    width: `${Math.max(width, 1.5)}%`,
                                                    background: `linear-gradient(90deg, ${lane.color} 0%, ${lane.color} 55%, transparent 100%)`,
                                                }}
                                                aria-hidden
                                            />
                                        );
                                    })()}

                                    {lane.events.map((event) => {
                                        const styles = statusStyles(event.status, lane.workflowKey);
                                        const isSelected = activeEvent?.id === event.id;
                                        const subject = event.label ?? event.preview ?? "Message";

                                        return (
                                            <div
                                                key={event.id}
                                                className="top-1/2 z-10 absolute -translate-x-1/2 -translate-y-1/2"
                                                style={{ left: leftPct(event.at) }}
                                                onMouseEnter={() => setActiveEvent(event)}
                                                onFocus={() => setActiveEvent(event)}
                                            >
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button
                                                            type="button"
                                                            className={`block rounded-full transition-transform ${
                                                                isSelected ? "w-5 h-5 scale-110" : "w-4 h-4"
                                                            } ${styles.dotClass}`}
                                                            style={styles.dotStyle}
                                                            aria-label={subject}
                                                            aria-pressed={isSelected}
                                                            onClick={() => setActiveEvent(event)}
                                                        />
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top" className="z-[60]">
                                                        <MessageEventTooltipBody
                                                            subject={subject}
                                                            at={event.at}
                                                            channel={event.channel}
                                                            laneLabel={lane.workflowName}
                                                        />
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {showSelectionPanel && activeEvent && (
                    <div className="flex flex-col gap-2 mt-4 pt-4 border-slate-100 border-t">
                        <div className="flex justify-between items-center gap-3">
                            <span className="text-slate-500 text-xs uppercase tracking-wide">Subject</span>
                            <span className="font-semibold text-slate-900 text-sm text-right">
                                {activeEvent.preview ?? activeEvent.label ?? "Message"}
                            </span>
                        </div>
                        <div className="flex justify-between items-center gap-3">
                            <span className="text-slate-500 text-xs uppercase tracking-wide">
                                {isAccountTimelineEvent(activeEvent) ? "Type" : "Workflow"}
                            </span>
                            <span className="flex items-center gap-2 font-medium text-slate-700 text-sm text-right">
                                {isAccountTimelineEvent(activeEvent) ? (
                                    <>
                                        <span className="bg-indigo-600 w-0.5 h-4 shrink-0" />
                                        Account event
                                    </>
                                ) : (
                                    <>
                                        <span
                                            className="rounded-full w-2.5 h-2.5 shrink-0"
                                            style={{
                                                backgroundColor: colorForWorkflow(activeEvent.workflowKey),
                                            }}
                                        />
                                        {activeEvent.workflowName ?? activeEvent.workflowKey}
                                    </>
                                )}
                            </span>
                        </div>
                        <div className="flex justify-between items-center gap-3">
                            <span className="text-slate-500 text-xs uppercase tracking-wide">Date</span>
                            <span className="font-medium tabular-nums text-slate-700 text-sm">
                                {dayjs(activeEvent.at).format("D MMM YYYY, HH:mm")}
                            </span>
                        </div>
                        {activeEvent.status && !isAccountTimelineEvent(activeEvent) && (
                            <div className="flex justify-between items-center gap-3">
                                <span className="text-slate-500 text-xs uppercase tracking-wide">Status</span>
                                <span className="font-medium text-slate-700 text-sm capitalize">
                                    {activeEvent.status}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </TooltipProvider>
    );
}

function SparkTimeline({
    events,
    domainStartMs,
    domainEndMs,
    showLines,
    className,
}: {
    events: MessageTimelineEvent[];
    domainStartMs?: number;
    domainEndMs?: number;
    showLines?: boolean;
    className?: string;
}) {
    const { series, openTails, domain, accountEvents } = useMemo(() => {
        const { messages, accountEvents: markers } = splitTimelineEvents(events);
        const sorted = [...messages].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

        const keys = [...new Set(sorted.map((event) => laneKeyForEvent(event)))];
        const points: PlotPoint[] = sorted.map((event) => ({
            id: event.id,
            x: new Date(event.at).getTime(),
            y: 1,
            at: event.at,
            workflowKey: laneKeyForEvent(event),
            workflowName: event.workflowName ?? event.workflowKey,
            label: event.label ?? "Message",
            preview: event.preview,
            channel: event.channel,
            fill: colorForWorkflow(event.workflowKey),
        }));

        const byWorkflow = keys.map((laneKey) => {
            const laneEvents = sorted.filter((event) => laneKeyForEvent(event) === laneKey);
            const baseWorkflowKey = laneEvents[0]?.workflowKey ?? laneKey;
            const workflowPoints = points.filter((point) => point.workflowKey === laneKey);
            return {
                workflowKey: laneKey,
                color: colorForWorkflow(baseWorkflowKey),
                points: workflowPoints,
            };
        });

        const allXs = [...points.map((point) => point.x), ...markers.map((event) => new Date(event.at).getTime())];
        const minX = domainStartMs ?? (allXs.length ? Math.min(...allXs) : Date.now());
        const maxX = domainEndMs ?? (allXs.length ? Math.max(...allXs) : Date.now());
        const pad = Math.max((maxX - minX) * 0.06, 86_400_000);
        const domainStart = minX - pad;
        const domainEnd = maxX + pad;
        const span = Math.max(domainEnd - domainStart, 1);

        const tails = byWorkflow.flatMap((group) => {
            const laneEvents = sorted.filter((event) => laneKeyForEvent(event) === group.workflowKey);
            const tail = openEndedTail(laneEvents, { start: domainStart, end: domainEnd, span });
            if (!tail || group.points.length === 0) return [];
            return [
                {
                    workflowKey: group.workflowKey,
                    color: group.color,
                    points: [
                        { x: tail.start, y: 1 },
                        { x: tail.end, y: 1 },
                    ],
                },
            ];
        });

        return {
            series: byWorkflow,
            openTails: tails,
            domain: [domainStart, domainEnd] as [number, number],
            accountEvents: markers,
        };
    }, [domainEndMs, domainStartMs, events]);

    if (!events.length) {
        return (
            <div className={className ?? "flex justify-center items-center w-52 h-10 text-muted-foreground text-xs"}>
                No messages
            </div>
        );
    }

    return (
        <div className={className ?? "w-52 h-10"}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart margin={{ top: 6, right: 6, left: 6, bottom: 6 }}>
                    <XAxis type="number" dataKey="x" domain={domain} hide />
                    <YAxis type="number" dataKey="y" domain={[0, 2]} hide width={0} />
                    <RechartsTooltip
                        content={<MessageTimelineTooltip />}
                        cursor={{ stroke: "#6366f1", strokeWidth: 2, strokeDasharray: "4 4" }}
                        isAnimationActive={false}
                    />
                    {accountEvents.map((event) => (
                        <ReferenceLine
                            key={event.id}
                            x={new Date(event.at).getTime()}
                            stroke={ACCOUNT_EVENT_STROKE}
                            strokeWidth={2.5}
                            strokeOpacity={0.9}
                            strokeDasharray="5 4"
                        />
                    ))}
                    {showLines !== false &&
                        series.map((group) =>
                            group.points.length > 1 ? (
                                <Line
                                    key={`line-${group.workflowKey}`}
                                    data={group.points}
                                    type="linear"
                                    dataKey="y"
                                    stroke={group.color}
                                    strokeWidth={3}
                                    strokeOpacity={0.75}
                                    dot={false}
                                    isAnimationActive={false}
                                    legendType="none"
                                />
                            ) : null
                        )}
                    {showLines !== false &&
                        openTails.map((tail) => (
                            <Line
                                key={`tail-${tail.workflowKey}`}
                                data={tail.points}
                                type="linear"
                                dataKey="y"
                                stroke={tail.color}
                                strokeWidth={3}
                                strokeOpacity={0.4}
                                strokeDasharray="6 4"
                                dot={false}
                                isAnimationActive={false}
                                legendType="none"
                            />
                        ))}
                    {series.map((group) => (
                        <Scatter
                            key={`scatter-${group.workflowKey}`}
                            data={group.points}
                            fill={group.color}
                            line={false}
                            isAnimationActive={false}
                            shape={(props: { cx?: number; cy?: number; payload?: PlotPoint }) => {
                                const { cx = 0, cy = 0, payload } = props;
                                return (
                                    <circle
                                        cx={cx}
                                        cy={cy}
                                        r={4.5}
                                        fill={payload?.fill ?? group.color}
                                        stroke="#fff"
                                        strokeWidth={2}
                                    />
                                );
                            }}
                        />
                    ))}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
}

function MessageTimelineChart({
    events,
    variant = "spark",
    className,
    domainStartMs,
    domainEndMs,
    showLines,
    showSelectionPanel = true,
}: MessageTimelineChartProps) {
    if (variant === "detail") {
        return (
            <DetailSwimlanes
                events={events}
                domainStartMs={domainStartMs}
                domainEndMs={domainEndMs}
                className={className}
                showSelectionPanel={showSelectionPanel}
            />
        );
    }

    return (
        <SparkTimeline
            events={events}
            domainStartMs={domainStartMs}
            domainEndMs={domainEndMs}
            showLines={showLines}
            className={className}
        />
    );
}

export default MessageTimelineChart;
